import { useState, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import { useApp } from '@/contexts/AppContext';
import { generateReportPDF, downloadCSV } from '@/utils/pdfUtils';
import {
  demoBookings,
  demoGuests,
  demoRooms,
  demoPayments,
  demoRoomBeds,
  demoBedBookings,
} from '@/mocks/demoData';

const COLORS = ['#1a2b4a', '#c9a84c', '#22c55e', '#ef4444', '#f59e0b', '#6b7280', '#8b5cf6'];

export default function BookingReports() {
  const { addToast } = useApp();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const fromDate = dateFrom ? new Date(dateFrom) : new Date('2000-01-01');
  const toDate = dateTo ? new Date(dateTo) : new Date('2100-01-01');

  const bookingsInRange = useMemo(() => {
    return demoBookings.filter((b) => {
      const d = new Date(b.created_at);
      return d >= fromDate && d <= toDate;
    });
  }, [fromDate, toDate]);

  const dailyBookings = useMemo(() => {
    const map = new Map<string, { walk_in: number; advance: number; pre_booking: number; online: number }>();
    bookingsInRange.forEach((b) => {
      const d = new Date(b.created_at).toISOString().slice(0, 10);
      const curr = map.get(d) || { walk_in: 0, advance: 0, pre_booking: 0, online: 0 };
      curr[b.booking_type as keyof typeof curr] = (curr[b.booking_type as keyof typeof curr] || 0) + 1;
      map.set(d, curr);
    });
    return Array.from(map.entries()).map(([date, counts]) => ({ date, ...counts })).sort((a, b) => a.date.localeCompare(b.date));
  }, [bookingsInRange]);

  const categoryOccupancy = useMemo(() => {
    const cats = [...new Set(demoRooms.map((r) => r.category))];
    return cats.map((cat) => {
      const total = demoRooms.filter((r) => r.category === cat).length;
      const occupied = demoRooms.filter((r) => r.category === cat && r.status === 'occupied').length;
      return { name: cat, total, occupied, rate: total > 0 ? Math.round((occupied / total) * 100) : 0 };
    });
  }, []);

  const roomRevenue = useMemo(() => {
    const map = new Map<string, number>();
    demoBookings.forEach((b) => {
      const room = demoRooms.find((r) => r.id === b.room_id);
      if (room) {
        map.set(room.room_number, (map.get(room.room_number) || 0) + b.total_amount);
      }
    });
    return Array.from(map.entries()).map(([room, revenue]) => ({ room, revenue })).sort((a, b) => b.revenue - a.revenue);
  }, []);

  const cancelledBookings = useMemo(() => {
    return demoBookings.filter((b) => b.status === 'cancelled');
  }, []);

  const handleGeneratePDF = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; dataKey: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    generateReportPDF(title, cols, rows, dateFrom && dateTo ? `Period: ${dateFrom} to ${dateTo}` : undefined);
    addToast('success', `${title} PDF downloaded`);
  };

  const handleDownloadCSV = (title: string, getRows: () => Record<string, string | number>[], getCols: () => { header: string; key: string }[]) => {
    const rows = getRows();
    const cols = getCols();
    if (rows.length === 0) {
      addToast('error', 'No data available for this report');
      return;
    }
    downloadCSV(title.replace(/\s+/g, '-'), cols, rows);
    addToast('success', `${title} CSV downloaded`);
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Booking Reports</h1>
        <p className="text-navy-500 mt-1">Occupancy, revenue, and cancellation analytics.</p>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">From Date</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-navy-500 mb-1.5">To Date</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Daily Bookings by Type */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Daily Bookings by Type</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Daily Booking Report', () => dailyBookings.map((d) => ({ date: d.date, walk_in: d.walk_in, advance: d.advance, pre_booking: d.pre_booking, online: d.online })), () => [{ header: 'Date', dataKey: 'date' }, { header: 'Walk-in', dataKey: 'walk_in' }, { header: 'Advance', dataKey: 'advance' }, { header: 'Pre-Booking', dataKey: 'pre_booking' }, { header: 'Online', dataKey: 'online' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Daily Booking Report', () => dailyBookings.map((d) => ({ date: d.date, walk_in: d.walk_in, advance: d.advance, pre_booking: d.pre_booking, online: d.online })), () => [{ header: 'Date', key: 'date' }, { header: 'Walk-in', key: 'walk_in' }, { header: 'Advance', key: 'advance' }, { header: 'Pre-Booking', key: 'pre_booking' }, { header: 'Online', key: 'online' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyBookings}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                <Bar dataKey="walk_in" stackId="a" fill="#1a2b4a" maxBarSize={30} />
                <Bar dataKey="advance" stackId="a" fill="#c9a84c" maxBarSize={30} />
                <Bar dataKey="pre_booking" stackId="a" fill="#22c55e" maxBarSize={30} />
                <Bar dataKey="online" stackId="a" fill="#f59e0b" maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-3 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-navy-800" /> Walk-in</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-gold-500" /> Advance</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-green-500" /> Pre-Booking</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-500" /> Online</span>
          </div>
        </div>

        {/* Category Occupancy */}
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-navy-800 text-sm">Occupancy by Category</h3>
            <div className="flex gap-1">
              <button onClick={() => handleGeneratePDF('Monthly Occupancy', () => categoryOccupancy.map((c) => ({ category: c.name, total: c.total, occupied: c.occupied, rate: `${c.rate}%` })), () => [{ header: 'Category', dataKey: 'category' }, { header: 'Total Rooms', dataKey: 'total' }, { header: 'Occupied', dataKey: 'occupied' }, { header: 'Rate', dataKey: 'rate' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
              <button onClick={() => handleDownloadCSV('Monthly Occupancy', () => categoryOccupancy.map((c) => ({ category: c.name, total: c.total, occupied: c.occupied, rate: `${c.rate}%` })), () => [{ header: 'Category', key: 'category' }, { header: 'Total Rooms', key: 'total' }, { header: 'Occupied', key: 'occupied' }, { header: 'Rate', key: 'rate' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryOccupancy} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} domain={[0, 100]} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number, n: string) => [n === 'rate' ? `${v}%` : v, n === 'rate' ? 'Occupancy Rate' : 'Rooms']} />
                <Bar dataKey="rate" fill="#1a2b4a" radius={[0, 4, 4, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Room-wise Revenue */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Room-wise Revenue</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Room-wise Income', () => roomRevenue.map((r) => ({ room: r.room, revenue: r.revenue })), () => [{ header: 'Room', dataKey: 'room' }, { header: 'Revenue (Rs.)', dataKey: 'revenue' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Room-wise Income', () => roomRevenue.map((r) => ({ room: r.room, revenue: r.revenue })), () => [{ header: 'Room', key: 'room' }, { header: 'Revenue (Rs.)', key: 'revenue' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={roomRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="room" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} formatter={(v: number) => [`Rs. ${v.toLocaleString('en-IN')}`, 'Revenue']} />
              <Bar dataKey="revenue" fill="#c9a84c" radius={[4, 4, 0, 0]} maxBarSize={32} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Booking History Table */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Booking History</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Booking History', () => bookingsInRange.map((b) => {
              const g = demoGuests.find((guest) => guest.id === b.guest_id);
              const r = demoRooms.find((room) => room.id === b.room_id);
              return { booking: b.booking_id, guest: g?.full_name || '', room: r?.room_number || '', type: b.booking_type, status: b.status, total: b.total_amount };
            }), () => [{ header: 'Booking ID', dataKey: 'booking' }, { header: 'Guest', dataKey: 'guest' }, { header: 'Room', dataKey: 'room' }, { header: 'Type', dataKey: 'type' }, { header: 'Status', dataKey: 'status' }, { header: 'Total', dataKey: 'total' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Booking History', () => bookingsInRange.map((b) => {
              const g = demoGuests.find((guest) => guest.id === b.guest_id);
              const r = demoRooms.find((room) => room.id === b.room_id);
              return { booking: b.booking_id, guest: g?.full_name || '', room: r?.room_number || '', type: b.booking_type, status: b.status, total: b.total_amount };
            }), () => [{ header: 'Booking ID', key: 'booking' }, { header: 'Guest', key: 'guest' }, { header: 'Room', key: 'room' }, { header: 'Type', key: 'type' }, { header: 'Status', key: 'status' }, { header: 'Total', key: 'total' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Booking ID</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Guest</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Room</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Type</th>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Status</th>
                <th className="text-right px-4 py-2 font-semibold text-navy-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {bookingsInRange.slice(0, 8).map((b) => {
                const g = demoGuests.find((guest) => guest.id === b.guest_id);
                const r = demoRooms.find((room) => room.id === b.room_id);
                return (
                  <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                    <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">{b.booking_id}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{g?.full_name || '-'}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{r?.room_number || '-'}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs capitalize">{b.booking_type.replace('_', ' ')}</td>
                    <td className="px-4 py-2.5 text-xs"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${b.status === 'checked_in' ? 'bg-green-100 text-green-700' : b.status === 'checked_out' ? 'bg-gray-100 text-gray-700' : b.status === 'confirmed' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{b.status.replace('_', ' ')}</span></td>
                    <td className="px-4 py-2.5 text-navy-700 text-xs text-right font-medium">Rs. {b.total_amount.toLocaleString('en-IN')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancellation Report */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Cancellation Report ({cancelledBookings.length})</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Cancellation Report', () => cancelledBookings.map((b) => ({ booking: b.booking_id, reason: b.cancellation_reason || 'N/A', refund: b.refund_status || 'N/A', total: b.total_amount })), () => [{ header: 'Booking ID', dataKey: 'booking' }, { header: 'Reason', dataKey: 'reason' }, { header: 'Refund Status', dataKey: 'refund' }, { header: 'Total', dataKey: 'total' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Cancellation Report', () => cancelledBookings.map((b) => ({ booking: b.booking_id, reason: b.cancellation_reason || 'N/A', refund: b.refund_status || 'N/A', total: b.total_amount })), () => [{ header: 'Booking ID', key: 'booking' }, { header: 'Reason', key: 'reason' }, { header: 'Refund Status', key: 'refund' }, { header: 'Total', key: 'total' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        {cancelledBookings.length === 0 ? (
          <div className="text-center py-8 text-navy-400 text-sm">No cancelled bookings in the system.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Booking ID</th>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Reason</th>
                  <th className="text-left px-4 py-2 font-semibold text-navy-700">Refund Status</th>
                  <th className="text-right px-4 py-2 font-semibold text-navy-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {cancelledBookings.map((b) => (
                  <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                    <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">{b.booking_id}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{b.cancellation_reason || 'Not specified'}</td>
                    <td className="px-4 py-2.5 text-navy-600 text-xs">{b.refund_status || 'Pending'}</td>
                    <td className="px-4 py-2.5 text-navy-700 text-xs text-right font-medium">Rs. {b.total_amount.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bed Occupancy Report */}
      <div className="bg-white rounded-xl border border-navy-100 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-navy-800 text-sm">Bed Occupancy Report</h3>
          <div className="flex gap-1">
            <button onClick={() => handleGeneratePDF('Bed Occupancy', () => {
              return demoRooms.filter((r) => r.room_type === 'dormitory' || r.room_type === 'shared').map((r) => {
                const beds = demoRoomBeds.filter((b) => b.room_id === r.id);
                const occ = beds.filter((b) => b.status === 'occupied').length;
                const avail = beds.filter((b) => b.status === 'available').length;
                return { room: r.room_number, total: beds.length, occupied: occ, available: avail, rate: `${Math.round((occ / beds.length) * 100)}%` };
              });
            }, () => [{ header: 'Room', dataKey: 'room' }, { header: 'Total Beds', dataKey: 'total' }, { header: 'Occupied', dataKey: 'occupied' }, { header: 'Available', dataKey: 'available' }, { header: 'Rate', dataKey: 'rate' }])} className="px-2 py-1 bg-navy-50 hover:bg-navy-100 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-pdf-line" /> PDF</button>
            <button onClick={() => handleDownloadCSV('Bed Occupancy', () => demoRooms.filter((r) => r.room_type === 'dormitory' || r.room_type === 'shared').map((r) => {
              const beds = demoRoomBeds.filter((b) => b.room_id === r.id);
              const occ = beds.filter((b) => b.status === 'occupied').length;
              const avail = beds.filter((b) => b.status === 'available').length;
              return { room: r.room_number, total: beds.length, occupied: occ, available: avail, rate: `${Math.round((occ / beds.length) * 100)}%` };
            }), () => [{ header: 'Room', key: 'room' }, { header: 'Total Beds', key: 'total' }, { header: 'Occupied', key: 'occupied' }, { header: 'Available', key: 'available' }, { header: 'Rate', key: 'rate' }])} className="px-2 py-1 border border-navy-200 hover:bg-navy-50 rounded text-xs text-navy-700 cursor-pointer whitespace-nowrap"><i className="ri-file-excel-line" /> Excel</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-50">
              <tr>
                <th className="text-left px-4 py-2 font-semibold text-navy-700">Room</th>
                <th className="text-center px-4 py-2 font-semibold text-navy-700">Total Beds</th>
                <th className="text-center px-4 py-2 font-semibold text-navy-700">Occupied</th>
                <th className="text-center px-4 py-2 font-semibold text-navy-700">Reserved</th>
                <th className="text-center px-4 py-2 font-semibold text-navy-700">Available</th>
                <th className="text-center px-4 py-2 font-semibold text-navy-700">Cleaning</th>
                <th className="text-right px-4 py-2 font-semibold text-navy-700">Occupancy %</th>
              </tr>
            </thead>
            <tbody>
              {demoRooms.filter((r) => r.room_type === 'dormitory' || r.room_type === 'shared').map((r) => {
                const beds = demoRoomBeds.filter((b) => b.room_id === r.id);
                const occ = beds.filter((b) => b.status === 'occupied').length;
                const resv = beds.filter((b) => b.status === 'reserved').length;
                const avail = beds.filter((b) => b.status === 'available').length;
                const clean = beds.filter((b) => b.status === 'cleaning').length;
                const maint = beds.filter((b) => b.status === 'maintenance').length;
                const rate = beds.length > 0 ? Math.round((occ / beds.length) * 100) : 0;
                return (
                  <tr key={r.id} className="border-t border-navy-100 hover:bg-navy-50/50">
                    <td className="px-4 py-2.5 text-navy-700 text-xs font-medium">
                      {r.room_number}
                      <span className="ml-2 text-xs text-navy-400">({r.category})</span>
                    </td>
                    <td className="px-4 py-2.5 text-center text-navy-700 text-xs font-medium">{beds.length}</td>
                    <td className="px-4 py-2.5 text-center text-navy-700 text-xs"><span className="text-red-600 font-medium">{occ}</span></td>
                    <td className="px-4 py-2.5 text-center text-navy-700 text-xs"><span className="text-yellow-600 font-medium">{resv}</span></td>
                    <td className="px-4 py-2.5 text-center text-navy-700 text-xs"><span className="text-green-600 font-medium">{avail + maint}</span></td>
                    <td className="px-4 py-2.5 text-center text-navy-700 text-xs"><span className="text-gray-500">{clean}</span></td>
                    <td className="px-4 py-2.5 text-right text-navy-700 text-xs">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 bg-navy-100 rounded-full h-1.5">
                          <div className="bg-navy-700 h-1.5 rounded-full" style={{ width: `${rate}%` }} />
                        </div>
                        <span className="font-medium">{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoBookings.length}</div>
          <div className="text-xs text-navy-500 mt-1">Total Bookings</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoRooms.filter((r) => r.status === 'occupied').length}</div>
          <div className="text-xs text-navy-500 mt-1">Occupied Rooms</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoRoomBeds.filter((b) => b.status === 'occupied').length}</div>
          <div className="text-xs text-navy-500 mt-1">Occupied Beds</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-gold-600">Rs. {demoPayments.reduce((s, p) => s + p.amount, 0).toLocaleString('en-IN')}</div>
          <div className="text-xs text-navy-500 mt-1">Total Revenue</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoRoomBeds.length}</div>
          <div className="text-xs text-navy-500 mt-1">Total Beds</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{demoRoomBeds.filter((b) => b.status === 'available').length}</div>
          <div className="text-xs text-navy-500 mt-1">Available Beds</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoBookings.filter((b) => b.is_bed_booking).length}</div>
          <div className="text-xs text-navy-500 mt-1">Bed Bookings</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-4 text-center">
          <div className="text-2xl font-bold text-navy-800">{demoBookings.filter((b) => b.pending_amount > 0).length}</div>
          <div className="text-xs text-navy-500 mt-1">Pending Payments</div>
        </div>
      </div>
    </div>
  );
}